type SecurityKind = "bidi" | "invisible" | "control";

export interface SecurityDescriptor {
  kind: SecurityKind;
  codePoint: string;
  shortName: string;
  name: string;
}

export interface SafeTextPart {
  text: string;
  security: SecurityDescriptor | null;
}

const BIDI_NAMES = new Map<number, readonly [string, string]>([
  [0x202a, ["LRE", "left-to-right embedding"]],
  [0x202b, ["RLE", "right-to-left embedding"]],
  [0x202c, ["PDF", "pop directional formatting"]],
  [0x202d, ["LRO", "left-to-right override"]],
  [0x202e, ["RLO", "right-to-left override"]],
  [0x2066, ["LRI", "left-to-right isolate"]],
  [0x2067, ["RLI", "right-to-left isolate"]],
  [0x2068, ["FSI", "first strong isolate"]],
  [0x2069, ["PDI", "pop directional isolate"]],
]);

const INVISIBLE_NAMES = new Map<number, readonly [string, string]>([
  [0x00ad, ["SHY", "soft hyphen"]],
  [0x034f, ["CGJ", "combining grapheme joiner"]],
  [0x061c, ["ALM", "Arabic letter mark"]],
  [0x1680, ["OGHAM SPACE", "Ogham space mark"]],
  [0x180e, ["MVS", "Mongolian vowel separator"]],
  [0x200b, ["ZWSP", "zero-width space"]],
  [0x200c, ["ZWNJ", "zero-width non-joiner"]],
  [0x200d, ["ZWJ", "zero-width joiner"]],
  [0x200e, ["LRM", "left-to-right mark"]],
  [0x200f, ["RLM", "right-to-left mark"]],
  [0x2028, ["LS", "line separator"]],
  [0x2029, ["PS", "paragraph separator"]],
  [0x202f, ["NNBSP", "narrow no-break space"]],
  [0x205f, ["MMSP", "medium mathematical space"]],
  [0x2060, ["WJ", "word joiner"]],
  [0x2061, ["FA", "function application"]],
  [0x2062, ["IT", "invisible times"]],
  [0x2063, ["IS", "invisible separator"]],
  [0x2064, ["IP", "invisible plus"]],
  [0x3000, ["IDEOGRAPHIC SPACE", "ideographic space"]],
  [0xfeff, ["BOM", "byte order mark / zero-width no-break space"]],
]);

function codePointLabel(codePoint: number): string {
  return `U+${codePoint.toString(16).toUpperCase().padStart(4, "0")}`;
}

function invisibleDescriptor(codePoint: number): { shortName: string; name: string } | null {
  const named = INVISIBLE_NAMES.get(codePoint);
  if (named) return { shortName: named[0], name: named[1] };
  if (codePoint >= 0x2000 && codePoint <= 0x200a) {
    return { shortName: "SPACE", name: "non-ASCII Unicode space" };
  }
  if (codePoint === 0x2065 || (codePoint >= 0x206a && codePoint <= 0x206f)) {
    return { shortName: "FORMAT", name: "invisible Unicode format control" };
  }
  return null;
}

export function securityDescriptor(character: string): SecurityDescriptor | null {
  const codePoint = character.codePointAt(0)!;
  const bidi = BIDI_NAMES.get(codePoint);
  if (bidi) {
    return {
      kind: "bidi",
      codePoint: codePointLabel(codePoint),
      shortName: bidi[0],
      name: bidi[1],
    };
  }
  const invisible = invisibleDescriptor(codePoint);
  if (invisible) {
    return {
      kind: "invisible",
      codePoint: codePointLabel(codePoint),
      ...invisible,
    };
  }
  const control =
    codePoint !== 0x09 &&
    (codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f));
  if (control) {
    return {
      kind: "control",
      codePoint: codePointLabel(codePoint),
      shortName: "CTRL",
      name: "control character",
    };
  }
  return null;
}

export function safeTextParts(text: string): SafeTextPart[] {
  const parts: SafeTextPart[] = [];
  let ordinary = "";
  for (const character of text) {
    const security = securityDescriptor(character);
    if (!security) {
      ordinary += character;
      continue;
    }
    if (ordinary) {
      parts.push({ text: ordinary, security: null });
      ordinary = "";
    }
    parts.push({
      text: `⟪${security.shortName} ${security.codePoint}⟫`,
      security,
    });
  }
  if (ordinary) parts.push({ text: ordinary, security: null });
  return parts;
}

export function appendSafeText(container: HTMLElement, text: string): void {
  safeTextParts(text).forEach((part) => {
    if (!part.security) {
      container.append(document.createTextNode(part.text));
      return;
    }
    const marker = document.createElement("span");
    marker.className = `unicode-control unicode-control-${part.security.kind}`;
    marker.textContent = part.text;
    marker.title = `${part.security.name} (${part.security.codePoint}); original character is not rendered`;
    container.append(marker);
  });
}

export function safePlainText(text: string): string {
  return safeTextParts(text)
    .map((part) => part.text)
    .join("");
}
